import React, { useState } from 'react';
import OptimizedImage from '../common/OptimizedImage';
import ImageModal from '../common/ImageModal';
import './Resume.css';

interface MediaItem {
  src: string;
  alt: string;
}

interface SimpleResumeItem {
  id: number;
  title: string;
  shortDescription?: string;
  description?: string | string[];
  images?: MediaItem[];
  logo?: string;
  logoAlt?: string;
}

type TabType = 'experience' | 'education' | 'achievement' | 'volunteering' | 'awards';

const Resume: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('experience');
  const [modalImage, setModalImage] = useState<string | null>(null);
  const [modalAlt, setModalAlt] = useState<string>('');
  const [currentGalleryImages, setCurrentGalleryImages] = useState<MediaItem[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);

  const experienceData = [
    {
      id: 0,
      title: "Sr. Technical Project Manager",
      company: "SysModeler, Inc.",
      companyUrl: "http://sysmodeler.ai/",
      logo: "/assets/img/others/sysmodeler_logo.jpg",
      logoAlt: "SysModeler, Inc. logo",
      employmentType: "Full-Time",
      locationType: "Remote",
      location: "United States",
      period: "July, 2026 - Present",
      description: "Over the past few years, I've had the opportunity to contribute as an AI Engineer, working alongside talented researchers and engineers on AI, Software Development and Systems Engineering projects. As our team continues to grow, my role is evolving toward technical leadership and project execution. I'm grateful for the trust placed in me and excited to support such a talented group of people as we continue building and scaling together.",
    },

    {
      id: 1,
      title: "Artificial Intelligence (AI) Engineer",
      company: "SysModeler, Inc.",
      companyUrl: "http://sysmodeler.ai/",
      logo: "/assets/img/others/sysmodeler_logo.jpg",
      logoAlt: "SysModeler, Inc. logo",
      employmentType: "Full-Time",
      locationType: "Remote",
      location: "United States",
      period: "October, 2025 - June, 2026",
      description: "SysModeler.ai is an advanced, cloud-native platform that leverages artificial intelligence to automate and accelerate Model-Based Systems Engineering (MBSE). It empowers engineers to instantly generate all 9 types of standard SysML diagrams by interpreting a variety of inputs, including natural language descriptions, code snippets from over 20 programming languages, and even real-time voice commands. By using powerful AI models, the tool translates complex system requirements into compliant visual models, which can then be refined using an intuitive drag-and-drop editor. This approach significantly speeds up the design process, improves collaboration, and simplifies documentation for engineers, particularly those working in safety-critical industries.",
    },

    

    {
      id: 2,
      title: "Artificial Intelligence (AI) Engineer",
      company: "RoboFication LLC",
      companyUrl: "http://robofication.net/",
      logo: "/assets/img/others/RoboFicationLogo.png",
      logoAlt: "RoboFication LLC logo",
      employmentType: "Full-Time",
      locationType: "Remote",
      location: "Detroit, Michigan, United States",
      period: "July, 2025 - Present",
      description: "RoboFication LLC is an US-based (Detroit, Michigan) company that specializes in automating systems engineering and certification processes for safety-critical industries like automotive, aerospace, defense, and robotics using AI and Formal Methods. Their AI-powered tools enhance efficiency by automating safety analyses, generating precise requirements, and ensuring regulatory compliance.",

    },

    {
      id: 3,
      title: "Junior AI Engineer",
      company: "RoboFication LLC",
      companyUrl: "http://robofication.net/",
      logo: "/assets/img/others/RoboFicationLogo.png",
      logoAlt: "RoboFication LLC logo",
      employmentType: "Full-Time",
      locationType: "Remote",
      location: "Detroit, Michigan, United States",
      period: "March, 2025 - July, 2025",
      description: "",
      responsibilities: [
        "Develop AI-driven automation tools for safety-critical industries",
        "Learn and implement advanced AI techniques to enhance existing systems",
        "Assist in developing natural language processing capabilities",
        "Support integration of AI functionalities into current applications",
        "Help design and automate workflows to minimize repetitive tasks",
        "Collaborate with other team members on various projects"
      ]
    },

    {
      id: 4,
      title: "Research Engineer",
      company: "BRAC University",
      companyUrl: "https://www.bracu.ac.bd/",
      logo: "/assets/img/others/BRACUniversityLogo.png",
      logoAlt: "BRAC University logo",
      employmentType: "Full-Time",
      locationType: "Hybrid",
      location: "Dhaka, Bangladesh",
      period: "February, 2024 - September, 2025",
      description: "BRAC University is a leading private university in Bangladesh, known for its innovative research and academic programs. The university focuses on interdisciplinary studies and aims to produce graduates who are not only knowledgeable but also socially responsible. The research initiatives at BRAC University emphasize real-world applications and community engagement.",
      responsibilities: [
        "Develop research protocols, pipeline, and methodology",
        "Process and analyze multiple types of raw data",
        "Automate research projects and fine-tune deep learning models",
        "Co-supervise and evaluate undergraduate thesis students",
        "Perform exam invigilation and lab classes when required"
      ],
      supervisors: [
        { name: "Dr. Farig Yousuf Sadeque", link: "https://scholar.google.com/citations?user=ULNaeowAAAAJ&hl=en" },
        { name: "Dr. Golam Rabiul Alam", link: "https://scholar.google.com/citations?user=t4GrJR4AAAAJ&hl=en" },
        { name: "Dr. S M Taiabul Haque", link: "https://scholar.google.com/citations?user=tMCRaaEAAAAJ&hl=en" },
        { name: "Dr. Swakkhar Shatabda", link: "https://scholar.google.com/citations?user=2DhrWFgAAAAJ&hl=en" },
      ],
      otherSupervisors: [
        { name: "Dr. Syed Ishtiaque Ahmed", link: "https://scholar.google.com/citations?user=A42gaP4AAAAJ&hl=en" },
        { name: "Dr. Nusrat Jahan Mim", link: "https://scholar.google.com/citations?user=HojHDRUAAAAJ&hl=en" },
        { name: "Dr. Ashik Ahmed", link: "https://scholar.google.com/citations?user=UodF0fIAAAAJ&hl=en" },
      ],
      projects: [
        "Bengali Speech Recognition, Diarization, and Synthesis",
        "Transliteration and standardization of Bengali Dialects",
        "Various Bengali Text-to-Speech projects",
        "Speech to IPA Conversion",
        "Speech Biometric System (Voice signature authentication)",
        "AI-Driven Agentic Agriculture Support System",
        "Bengali humor and cultural roots with agentic AI",
        "Algorithmic Amnesia: An Empirical Study of Bengali Folklore Generation in Large Language Models",
        "Integrating Large Language Models for Intelligent Fault Diagnosis and Remedial Strategy Development in Power Transmission Systems",
        "AI-Driven Insights into Microfiber Pollution: Employing Large Language Models for Source Identification and Remediation",
      ]
    },

    {
      id: 4,
      title: "Freelance Data Scientist",
      company: "Freelance",
      companyUrl: "",
      logo: "https://cdn.worldvectorlogo.com/logos/freelancer-1.svg",
      logoAlt: "Freelancer logo",
      employmentType: "Freelance",
      locationType: "Remote",
      location: "Dhaka, Bangladesh",
      period: "July, 2022 - Present",
      description: `I sometimes do freelance Machine Learning, Deep Learning, Data Science, or any AI domain-related projects to challenge and hone my technical skills.

So far, I have pulled off a substantial amount of gigs/tasks given to me by clients from not only from Bangladesh, but also from foreign countries (US, UK & Germany to be more specific).`
    },

       {
      id: 5,
      title: "Instructor",
      company: "Lead Academy",
      companyUrl: "https://www.lead.academy/",
      logo: "/assets/img/others/LeadAcademyLogo.jpg",
      logoAlt: "Lead Academy logo",
      employmentType: "Full-Time",
      locationType: "Hybrid",
      location: "Dhaka, Bangladesh",
      period: "October, 2023 - December, 2023",
      description: `Lead Academy approached me to design and take a NLP course by developing relevant pre-recorded contents for the course and I accepted the offer.

I worked as an instructor there on a contractual basis. So far, I have only developed one course titled "<a href="https://lead.academy/course/natural-language-processing-nlp-for-beginners-online-course" target="_blank" rel="noopener noreferrer">Natural Language Processing (NLP) for Beginners using Python</a>" where I covered all the relevant topics one might need to get started with Natural Language Processing (NLP), from the very basics of NLP to developing deep learning models and using few existing large language models. 
Apart from covering the theories, I also demonstrated the concepts practically using the programming language Python and also demonstrated the usage of a few other relevant tools and platforms.`
    },

           {
      id: 6,
      title: "Coding Instructor",
      company: "Learn Time",
      companyUrl: "https://www.learntime.com.bd/math",
      logo: "/assets/img/others/learntimeLogo.jpg",
      logoAlt: "Learn Time logo",
      employmentType: "Part-Time",
      locationType: "Remote",
      location: "Rajshahi, Bangladesh",
      period: "November, 2021 - December, 2022",
      description: `LearnTime is a Rajshahi-based e-learning platform. This is a remote part-time job where I provide 3-4 hours a week.`,
      responsibilities: [
        "Teach programming (With Python) to people(of all ages, especially kids) from non-programming backgrounds who have little to no prior programming experience.",
        "Make notes, and documents, and set problems for the tests.",
        "Provide solutions to the problems students are struggling withh",
        "Teach data analytics using Python to interested people."
      ]
    },

    {
      id: 7,
      title: "Chief Author & Coordinator (Music Segment)",
      company: "নিয়ন আলোয় - Neon Aloy",
      companyUrl: "https://www.linkedin.com/company/neonaloy/posts/?feedView=all",
      logo: "/assets/img/others/neonaloy_logo.jpg",
      logoAlt: "Neon Aloy logo",
      employmentType: "Part-Time",
      locationType: "Remote",
      location: "Sylhet, Bangladesh",
      period: "March, 2019 - December, 2019",
      description: `Neon Aloy was a Sylhet based online portal founded by 4 SUST graduates. I worked at a online bangla portal or Magazine named "NEON ALOY". I started as a Coordinator of the music segment in September 2018, and with proven experience and dedication, I was promoted to Chief Author & Coordinator in March 2019, leading the entire music content strategy and team.`,
      responsibilities: [
          "Train and proofread new authors' work and provide feedback",
          "Coordinate with the editorial team for content planning",
          "Manage the music segment's content calendar",
          "Write music articles and song/album reviews of local and international artists",
          "Interview local artists and write follow-up reports",
          "Organize online events and webinars featuring local artists"
        ]
    },

    {
      id: 7,
      title: "Sr. Content Writer & Coordinator (Music Segment)",
      company: "নিয়ন আলোয় - Neon Aloy",
      companyUrl: "https://www.linkedin.com/company/neonaloy/posts/?feedView=all",
      logo: "/assets/img/others/neonaloy_logo.jpg",
      logoAlt: "Neon Aloy logo",
      employmentType: "Part-Time",
      locationType: "Remote",
      location: "Sylhet, Bangladesh",
      period: "September, 2018 - February, 2019",
      description: `After some time as a Content Writer, I was promoted to Senior Content Writer for my dedication and contributions, and later took on the additional role of Music Segment Coordinator, where I oversaw all music-related content and led efforts to strengthen the publication’s presence in this domain.`,
      responsibilities: [
        "Coordinated the contents and managed a team of content writers in crafting engaging music articles and reviews",
        "Oversaw music articles, reviews, and event coverage to ensure editorial excellence",
        "Coordinated with photographers, writers, and editors for comprehensive music stories",
        "Researched and approved new content ideas aligned with audience interests",
        "Managed deadlines and weekly publication schedules"
      ]
    },

    {
      id: 8,
      title: "Content Writer",
      company: "নিয়ন আলোয় - Neon Aloy",
      companyUrl: "https://www.linkedin.com/company/neonaloy/posts/?feedView=all",
      logo: "/assets/img/others/neonaloy_logo.jpg",
      logoAlt: "Neon Aloy logo",
      employmentType: "Part-Time",
      locationType: "Remote",
      location: "Sylhet, Bangladesh",
      period: "March, 2018 - August, 2018",
      description: `After completing my trainee period, I was promoted to a full Content Writer role, focusing primarily on music-related content and establishing my expertise in the field.`,
      responsibilities: [
        "Write high-quality music articles and reviews",
        "Cover local and international music events",
        "Research and write about music trends and industry news",
        "Collaborate with editorial team on content strategy",
        "Meet weekly content publication targets"
      ]
    },

    {
      id: 9,
      title: "Trainee Content Writer",
      company: "নিয়ন আলোয় - Neon Aloy",
      companyUrl: "https://www.linkedin.com/company/neonaloy/posts/?feedView=all",
      logo: "/assets/img/others/neonaloy_logo.jpg",
      logoAlt: "Neon Aloy logo",
      employmentType: "Part-Time",
      locationType: "Remote",
      location: "Sylhet, Bangladesh",
      period: "November, 2017 - February, 2018",
      description: `I started my journey at Neon Aloy as a trainee content writer, learning the fundamentals of bangla content writing. I learned the underrated hacks and minuscule details of writing during this period. This is where I developed my content writing skills and also fast typing skills. This was also a part time job that I used to do besides academic works & study.`,
      responsibilities: [
        "Learn content writing fundamentals and best practices",
        "Write trainee-level articles under supervision",
        "Research on various topics and trends",
        "Assist senior writers and editor with content research and proof reading",
        "Participate in editorial meetings and training sessions"
      ]
    },
  ];

  // Grouping is handled later in the render for the experience tab to avoid duplicate computations.

  const educationData = [
    {
      id: 1,
      degree: "Master of Science - M.Sc",
      field: "Computer Science Engineering",
      institution: "BRAC University",
      location: "Dhaka, Bangladesh",
      logo: "/assets/img/others/BRACUniversityLogo.png",
      logoAlt: "BRAC University logo",
      period: "2021 - 2023",
      thesis: {
        title: "A character gram modeling approach towards Bengali Speech to Text with Regional Dialects",
        link: "https://dspace.bracu.ac.bd/xmlui/handle/10361/25982"
      },
      supervisor: {
        name: "Dr. Golam Rabiul Alam",
        link: "https://scholar.google.com/citations?user=t4GrJR4AAAAJ&hl=en"
      },
      publication: {
        title: "Are ASR foundation models generalized enough to capture features of regional dialects for low-resource languages?",
        link: "https://aclanthology.org/2025.ijcnlp-short.17/"        
      },
      concentration: "Natural Language Processing & Speech Recognition",
      courses: [
        "CSE710: Advanced Artificial Intelligence",
        "CSE711: Symbolic Machine Learning",
        "CSE712: Natural Language Processing",
        "CSE713: Synthetic Pattern & Speech Recognition",
        "CSE715: Neural Networks & Fuzzy Systems",
        "CSE799: Data Science"
      ]
    },


    {
      id: 2,
      degree: "Bachelor of Science - B.Sc",
      field: "Electrical and Electronics Engineering",
      institution: "BRAC University",
      location: "Dhaka, Bangladesh",
      logo: "/assets/img/others/BRACUniversityLogo.png",
      logoAlt: "BRAC University logo",
      period: "2016 - 2020",
      thesis: {
        title: "Efficient approach for reliability evaluation of the BUS4 distribution system considering momentary interruption. ",
        link: "https://dspace.bracu.ac.bd/xmlui/handle/10361/14487"
      },
      supervisor: {
        name: "Dr. A.S. Nazmul Huda",
        link: "https://scholar.google.com/citations?user=-QIR2lUAAAAJ&hl=en"
      },
      publication: {
        title: "Monte Carlo Simulation for Reliability Worth Assessment of Distribution System Considering Momentary Interruptions",
        link: "https://ieeexplore.ieee.org/document/10044904/"
      },
      concentration: "Networking, Signal Processing & Wireless Technologies",
      courses: [
        "EEE201 | EEE202: Electrical Circuits I | Electrical Circuits I Laboratory",
        "EEE203 | EEE204: Electrical Circuits II | Electrical Circuits II Laboratory",
        "EEE205 | EEE206: Electrical Devices I | Electrical Devices I Laboratory",
        "EEE207 | EEE208: Electrical Devices II | Electrical Devices II Laboratory",
        "EEE209: Semiconductor Devices and Materials",
        "EEE221: Energy Conversion I",
        "EEE223 | EEE224: Energy Conversion II | Energy Conversion II Laboratory",
        "EEE241: Electromagnetic Fields and Waves",
        "EEE243: Signal and Systems",
        "EEE301 | EEE302: Digital Electronics | Digital Electronics Laboratory",
        "EEE305 | EEE306: Control Systems | Control Systems Laboratory",
        "EEE341 | EEE342: Introduction to Communication Engineering | Introduction to Communication Engineering Laboratory",
        "EEE343 | EEE344: Digital Signal Processing | Digital Signal Processing Laboratory",
        "EEE361 | EEE362: Data Communication | Data Communication Laboratory",
        "EEE365 | EEE366: Microprocessor and Interfacing | Microprocessor and Interfacing Laboratory",
        "EEE411 | EEE412: VLSI Design | VLSI Design Laboratory",
        "EEE445 | EEE446: Digital Communications | Digital Communications Laboratory",
        "EEE455 | EEE456: Fundamentals of Wireless LAN | Fundamentals of Wireless LAN Laboratory"
      ]
    }
  ];

  // New tabs data (dummy/sample items)
  const achievementData: SimpleResumeItem[] = [
        {
      id: 1,
      title: "International English Language Testing System (IELTS) Academic",
      shortDescription: "Issued by British Council\nDecember 2025",
      logo: "https://ielts.org/cdn/ielts-and-partner-logos/british-council-logo.webp?fit=cover&height=370&width=370&s=HTciCjfBUwMOFDTB4dMO71XWvW6VvoqvYiJF48BEWXI",
      logoAlt: "British Council logo",
      description: `<p>Did a very rushed prep in the final 1.5 days (Basically the last weekend), and honestly couldn’t have pulled it off without 2 of my favorite juniors. 

Grateful to <a href="https://www.linkedin.com/in/u-mong-sain-chak-b055241a6/" target="_blank" rel="noopener noreferrer">U Mong Sain Chak</a> for the last-minute push when I needed it the most, and to <a href="https://www.linkedin.com/in/rubayet-sabbir-faruque/" target="_blank" rel="noopener noreferrer">Rubayet Sabbir Faruque</a> for being my mock examiner for all the speaking practice and honest feedback.</p>`,
      images: [
        { 
          src: "/assets/img/others/ielts_score.jpg", 
          alt: "" 
        }
      ]
    },
    {
      id: 1,
      title: "Runners up - National AI Hackathon",
      shortDescription: "Issued by Bangladesh Innovation Conclave\nMay 2025",
      logo: "/assets/img/others/bangladesh_innovation_conclave_logo.jpg",
      logoAlt: "Bangladesh Innovation Conclave logo",
      description: `<p>Participated in the National AI Hackathon with my team (<a href="https://www.linkedin.com/in/tontus/?originalSubdomain=bd" target="_blank" rel="noopener noreferrer" style="color: #0073b1; text-decoration: none;">MD. Shaleh Islam Tonmoy</a> bhai, <a href="https://www.linkedin.com/in/tanmoy-shome/?originalSubdomain=bd" target="_blank" rel="noopener noreferrer" style="color: #0073b1; text-decoration: none;">Tanmoy Shome</a>, <a href="https://www.linkedin.com/in/rawhatur-rabbi-rafin/?originalSubdomain=bd" target="_blank" rel="noopener noreferrer" style="color: #0073b1; text-decoration: none;">Rawhatur Rabbi</a>, and <a href="https://www.linkedin.com/in/ruwad-naswan-612673245/?originalSubdomain=bd" target="_blank" rel="noopener noreferrer" style="color: #0073b1; text-decoration: none;">Ruwad Naswan</a>) named "AgriBRACUion". We participated under the Agriculture category, and we present AgriBuddy, an AI-powered, agentic support system designed to provide personalized and real-time agricultural guidance to farmers in Bangladesh. AgriBuddy combines advanced language and vision models, including a Retrieval-Augmented Generation (RAG) framework, to deliver Bangla-language recommendations through a smart chatbot.</p>

<p><strong>It supports:</strong></p>
<ul>
<li>Natural Bangla or Banglish text queries.</li>
<li>Image-based rice disease detection using CNN.</li>
<li>Context-aware farming advice tailored to the user's region, crop, and conditions.</li>
</ul>

<p><strong>Core Features:</strong></p>
<ul>
<li>Multilingual query understanding (Bangla, English, Banglish)</li>
<li>Rice disease identification from images (Total 10 classes: 9 diseases and 1 for normal case scenario)</li>
<li>Expert agricultural advisory via integrated knowledge base</li>
<li>Personalized recommendations using user profiles and memory agents</li>
<li>Tools for weather, soil condition analysis, and cultivation tips</li>
</ul>

<p><strong>Technical Highlights:</strong></p>
<ul>
<li>Modular agent-based design with Smart Query, Memory, and Expert Agents</li>
<li>CNN-based disease detection trained on the Paddy Doctor dataset</li>
<li>RAG-powered answer generation with Bangla embeddings</li>
<li>Deployment via a mobile-first Progressive Web App (PWA).</li>
</ul>

<p>Our idea was shortlisted and made it into the finals to compete against other teams with some excellent ideas as well. We didn't win first place, but we were really close. Also, the entire experience was everything. Brainstorming for ideas, coding all night after the daytime job, debugging, and lots of heuristic learning.</p>

<p>Also, a big shoutout to our professor <a href="https://scholar.google.com/citations?user=2DhrWFgAAAAJ&hl=en" target="_blank" rel="noopener noreferrer">Dr. Swakkhar Shatabda</a> sir for motivating us to begin with. This project wouldn't have taken shape if Swakkhar Sir hadn't given us the initial push.</p>`,
      images: [
        { 
          src: "/assets/img/others/pic_1.jpg", 
          alt: "AI Hackathon Team Photo" 
        },
        { 
          src: "/assets/img/others/ss.PNG" , 
          alt: "AI Hackathon Presentation" 
        },
        { 
          src: "/assets/img/others/ht3.jpg", 
          alt: "AI Hackathon Award Ceremony" 
        },
        { 
          src: "/assets/img/others/ht4.jpg", 
          alt: "AI Hackathon Team Discussion" 
        },
        { 
          src: "/assets/img/others/ht5.jpg", 
          alt: "AI Hackathon Project Demo" 
        },
        { 
          src: "/assets/img/others/ht6.jpg", 
          alt: "AI Hackathon Final Results" 
        }
      ]
    },
    {
      id: 2,
      title: "Contributed to a Google Project",
      shortDescription: "Issued by Google\nDecember 2023",
      logo: "https://www.freepnglogos.com/uploads/google-logo-png/google-logo-png-webinar-optimizing-for-success-google-business-webinar-13.png",
      logoAlt: "Google logo",
      description: `<p>Google approached our (Bengali.AI) founder <a href="https://www.linkedin.com/in/ahmed-imtiaz-prio/" target="_blank" rel="noopener noreferrer">Ahmed Imtiaz Humayun (Prio)</a> bhai for a possible collaboration with members of the Bengali.AI team with a proposal to conduct a research project related to our very own mother tongue, the language Bengali.</p>

<p>A small team was formed for this and I was one of the selected team members. Wrapped up the project within few months and submitted the work successfully to Google.</p>  

<p>It was fun working on this project. Got some better insights into my own mother tongue as well as got an initial idea of how LLM research works on Google.</p>

<p>So yeah, Officially worked & contributed to a Google project.</p>`,
      images: [
        { 
          src: "https://diplo-media.s3.eu-central-1.amazonaws.com/2024/10/google-ai-security-update-remote-phone-locking.png", 
          alt: "" 
        }
      ]
    },
    {
      id: 3,
      title: "Invitee – Google Foo Bar Challenge",
      shortDescription: "Issued by Google\nJuly 2023",
      logo: "https://www.freepnglogos.com/uploads/google-logo-png/google-logo-png-webinar-optimizing-for-success-google-business-webinar-13.png",
      logoAlt: "Google logo",
      description: `<p>Back in 2023, while coding for a research project, I Googled the Lambda function syntax and unexpectedly got invited to Google's Foo Bar Challenge. Google's Foo Bar Challenge is an invitation-only secret recruitment program for Google career opportunities, events, and programs.</p>

<p>The program was shut down in April 2024.</p>

<p><strong>Note:</strong> Einstein's quote, "Never memorize what you can look up in books," is a cornerstone of my learning philosophy. I hardly even try to memorize something that I know I can look up to at any time. I remember the process, not the syntax. Even as a daily data manipulation library Pandas user, I look things up as needed rather than overloading my memory with syntax. I believe if it's available out there, there's no point in remembering the syntax, as I'm just a human.</p>`,
      images: [
        { 
          src: "/assets/img/others/foo1.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/foo2.jpg", 
          alt: "" 
        }
      ]
    },
    {
      id: 4,
      title: "Winner - Photography Contest",
      shortDescription: "Issued by Fotoboss\nJuly 2023",
      logo: "https://www.fotoboss.cz/wp-content/uploads/2022/04/fotobosss-logo.png",
      logoAlt: "Fotoboss logo",
      description: "Won the photography contest themed \"All about Green\" by Fotoboss.",
      images: [
        { 
          src: "/assets/img/others/winp1.jpg", 
          alt: "Winning Photo" 
        },
        { 
          src: "/assets/img/others/winp2.jpg", 
          alt: "Mail Screenshot" 
        }
      ]
    },
    {
      id: 5,
      title: "100% Merit Based Scholarship Waiver for Summer 2022",
      shortDescription: "Issued by BRAC University\nApril 2022",
      logo: "https://www.bracu.ac.bd/sites/all/themes/sloth/images/f-logo.svg",
      logoAlt: "BRAC University logo",
      description: "Received a 100% tuition fee waiver as a merit scholarship based on academic results (CGPA: 4.00/4.00) up to the semester of Summer 2022."
    },
    {
      id: 6,
      title: "100% Merit Based Scholarship Waiver for Spring 2022",
      shortDescription: "Issued by BRAC University\nJanuary 2022",
      logo: "https://www.bracu.ac.bd/sites/all/themes/sloth/images/f-logo.svg",
      logoAlt: "BRAC University logo",
      description: "Received a 100% tuition fee waiver as a merit scholarship based on academic results (CGPA: 4.00/4.00) up to the semester of Spring 2022."
    },
    {
      id: 7,
      title: "Vice Chancellor's List",
      shortDescription: "Issued by BRAC University\nApril 2020",
      logo: "https://www.bracu.ac.bd/sites/all/themes/sloth/images/f-logo.svg",
      logoAlt: "BRAC University logo",
      description: "Got into the Vice Chancellor's list for achieving a GPA of 4.00 (out of a scale of 4.00) with 18 credits in the semester SPRING 2020.",
      images: [
        { 
          src: "https://i.ibb.co.com/Mx3JDt5m/Capture.png", 
          alt: "" 
        }
      ]
    }
  ];

  const volunteeringData: SimpleResumeItem[] = [
    {
      id: 1,
      title: "AI/ML Researcher | Assistant Co-ordinator",
      shortDescription: "Bengali.AI",
      logo: "assets/img/others/bai_logo.PNG",
      logoAlt: "Bengali.AI logo",
      description: `<p>Founded in 2018, Bengali.AI has been developing open-source AI language tools & technologies for the Bengali language for common people to use as well as resources for both deep learning and linguistic researchers to comprehend and understand the language better from both computational and linguistic aspects.</p>

<p>It is a non-profit initiative mostly working to solve the absence of open-sourced datasets and tools for Bengali Computer Vision/Natural Language Processing/Speech recognition, and Computational linguistic research with the ultimate aspiration of lifting our mother tongue Bengali from being a low-resourced language someday.</p>

<p>I mostly work on speech projects and with audio data.</p>

<p>Eg: Bengali speech-to-text with regional accents and dialects (Currently leading this project), Indigenous-speech-text and speech-to-IPA transcriptions, etc.</p>

<p>And different other projects as well and contribute however I can on those projects with both my technical and soft skills.</p>`,
      images: [
        { 
          src: "https://i.ibb.co.com/39BmKYVp/504277869-10222684872909678-3265587526765701363-n.jpg", 
          alt: "Bengali.AI team photo" 
        },
        { 
          src: "/assets/img/others/bai1.jpg", 
          alt: "Bengali.AI media photo" 
        },
        { 
          src: "/assets/img/others/bai2.jpg", 
          alt: "Bengali.AI media photo" 
        },
        { 
          src: "/assets/img/others/bai_meetup.jpg", 
          alt: "Bengali.AI media photo" 
        }
      ]
    },
        {
      id: 2,
      title: "Workshop Instructor - A hands-on guide to AI tools for non-technical workflows",
      shortDescription: "Bangladesh Agricultural University • June 2026",
      logo: "/assets/img/others/bau_logo.jpg",
      logoAlt: "Bangladesh Agricultural University logo",
      description: `
      
      <p>Invited to Bangladesh Agricultural University (BAU), Mymensingh, to discuss the current state of Bangla in AI as part of a panel discussion. Additionally, served as a</p>
      <p>workshop instructor, demonstrating how AI can be productively leveraged to automate non-technical workflows and everyday tasks. It was an honor to share the stage </p>
      <p> with this prominent group of Bangla NLP researchers. Special thanks to <a href="https://www.linkedin.com/company/baulc20/" target="_blank" rel="noopener noreferrer"> Bangladesh Agricultural University Language Club (BAULC) </a> for the invitation and hospitality. </p>`,
images: [
        { 
          src: "/assets/img/others/bau_poster.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/bau_workshop_1.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/bau_workshop_2.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/bau_workshop_3.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/bau_workshop_4.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/bau_workshop_5.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/bau_workshop_6.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/bau_workshop_7.jpg", 
          alt: "" 
        },
        
      ]
    },
    {
      id: 2,
      title: "Workshop Host & Mentor - National AI Workshop on Speech Recognition",
      shortDescription: "Islamic University of Technology • March 2024",
      logo: "/assets/img/others/iut_logo.jpg",
      logoAlt: "Islamic University of Technology logo",
      description: `<p>Bengali.AI, along with IUT Computer Society(IUTCS), is running a nationwide AI competition called "ভাষা-বিচিত্রা" (Language Variety) to find the next big talents in speech recognition for regional dialects.</p>

<p>Despite advancements in Automatic Speech Recognition (ASR), our findings indicate limitations in current technology for accurate zero-shot and fine-tuning performance on local dialects. This challenge highlights a critical area for further research and development. This datathon targets this gap by seeking solutions that enhance ASR capabilities for diverse Bengali dialects, a language lacking extensive technological support for these regional variations.</p>

<p>Along with me & my teammates from Bengali.AI, "Team Apocalypse" will also took the workshop with us side by side.</p>`,
images: [
        { 
          src: "/assets/img/others/bai4.jpg", 
          alt: "" 
        }
      ]
    },
    {
      id: 3,
      title: "Workshop Host & Mentor – NLP Datathon Bootcamp",
      shortDescription: "United International University • February 2024",
      logo: "/assets/img/others/uiu_logo.png",
      logoAlt: "United International University logo",
      description: `<p>Took a workshop on behalf of Bengali.AI along with some of my teammates for the inter-university natural language processing competition's bootcamp, organized by UIUCCL</p>`,
      images: [
        { 
          src: "/assets/img/others/bai5.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/bai6.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/bai7.jpg", 
          alt: "" 
        }     
      ]
    },
        {
      id: 4,
      title: "Manuscript Reviewer",
      shortDescription: "Institute of Electrical and Electronics Engineers (IEEE) • September 2025",
      logo: "/assets/img/others/ieee_logo.png",
      logoAlt: "ieee reviewer certificate logo",
      description: `<p>Honored to have been invited to serve as a paper reviewer by the organizing committee of the IEEE International Conference on Advanced Computing Technologies (ICACT 2025). <br> <br> Had the opportunity to review papers focusing on AI-driven innovations and contributing to maintaining the conference’s academic quality and integrity.</p>`,
      images: [
        { 
          src: "/assets/img/others/ieee_reviewer.PNG", 
          alt: "" 
        }   
      ]
    },
    {
      id: 5,
      title: "Blood Donor",
      shortDescription: "Quantum Foundation • February 2021 - Present",
      logo: "/assets/img/others/quantum_foundation_logo.png",
      logoAlt: "Quantum Foundation logo",
      description: `<p>I donate my blood here in every 4 months. <br> I’ve lost count of how many times I’ve donated blood, whether it was after a sudden phone call or a post that showed up on Facebook feed.</p>`,
      images: [
        { 
          src: "/assets/img/others/qf1.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/qf2.jpg", 
          alt: "" 
        }    
      ]
    },
    {
      id: 6,
      title: "Content Moderator and Musician",
      shortDescription: "Hawai Mithaiyar Gan • January 2019 - December 2020",
      logo: "/assets/img/others/hmg_logo.jpg",
      logoAlt: "Hawai Mithaiyar Gan logo",
      description: `<p>Hawai Mithaiyar Gan is basically a duo of 2 multi-instrumentalist "Murshedul Arefin Riyad" & "Tasin Bin Noor" and also a platform for musicians from different genres to collaborate.</p>

<p>I used to plan, create, and moderate the content of their YouTube channel and social media platforms and sometimes also contributed to music.</p>

<p>Youtube Channel: <a href="https://www.youtube.com/channel/UC7f0CqF5fqOq5Qtb1_mxC8g" target="_blank" rel="noopener noreferrer" class="youtube-link">https://www.youtube.com/@hawaimithaiiyergan8330</a></p>`,
      images: [
        { 
          src: "/assets/img/others/hmg_1.jpg", 
          alt: "" 
        }
      ]
    }
  ];

  const awardsData: SimpleResumeItem[] = [
    {
      id: 1,
      title: "Adjudicator",
      shortDescription: "Bangladesh University of Engineering and Technology\nMarch 2026",
      logo: "/assets/img/others/BUETLogo.png",
      logoAlt: "BUET logo",
      description: `<p> Got invited to be a adjudicator for the national deep learning competition "DLSprint4" organized by CSE department of BUET. The problem was bengali Long-Form audio transcription & Multi-Speaker diarization.</p>`,
      images: [
        { 
          src: "/assets/img/others/adj11.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/adj12.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/adj13.jpg", 
          alt: "" 
        },
      ]    
    },
        {
      id: 2,
      title: "Adjudicator",
      shortDescription: "Islamic University of Technology\nApril 2024",
      logo: "/assets/img/others/iut_logo.jpg",
      logoAlt: "Islamic University of Technology logo",
      description: `<p>I'm pleased to announce that the dataset that was utilized in this national ICT fest's datathon segment "Bhashabichitra: ASR for Regional Dialects" was an extended version of the dataset that I developed from scratch as a part of my master's thesis research which I later extended with my team here in Bengali.AI</p>

<p>Huge thanks to the IUT Computer Society(IUTCS) for orchestrating such a fantastic event. Grateful to have been a part of such a grand national event.</p>`,
      images: [
        { 
          src: "/assets/img/others/adj2.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/adj3.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/adj4.jpg", 
          alt: "" 
        },
        { 
          src: "https://i.ibb.co.com/fzm10bSp/504158011-10222700763666937-3820733829808306677-n.jpg", 
          alt: "" 
        }
      ]
    },
    {
      id: 3,
      title: "Adjudicator",
      shortDescription: "United International University\nMarch 2024",
      logo: "/assets/img/others/uiu_logo.png",
      logoAlt: "United International University logo",
      description: `<p>I'm pleased to announce that the dataset that was utilized in this national level datathon "Bhashamul: Bengali Text with Regional Dialects to IPA Transcription Modeling" was a prototype dataset from one of the researches I'm currently leading here with my team at Bengali.AI.</p>

<p>Thanks to UIU Cse department & UIU Computer Club for organizing such an amazing event. Glad to have been able to be a part of this datathon.</p>`,
      images: [
        { 
          src: "/assets/img/others/adj5.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/adj6.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/adj7.jpg", 
          alt: "" 
        },
        { 
          src: "https://i.ibb.co.com/whrnxWgC/503511178-10222684872989680-1500352243817215248-n.jpg", 
          alt: "" 
        }
      ]
    },
    {
      id: 4,
      title: "Adjudicator",
      shortDescription: "University of Dhaka\nNovember 2023",
      logo: "/assets/img/others/duLogo.jpg",
      logoAlt: "University of Dhaka logo",
      description: `<p>One of my recent co-authored research paper's dataset (Bengali text to IPA transcription) was utilized in the national deep learning competition "Datathon Challenge - Cefalo Presents ITverse 2023" arranged by Dhaka University's "IIT Software Engineers' Community" and I was invited to be one of the judges of the competition on behalf of Bengali.AI and evaluate the submissions of the top participant teams from all over the Bangladesh.</p>`,
      images: [
        { 
          src: "/assets/img/others/adj8.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/adj9.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/adj10.jpg", 
          alt: "" 
        },

      ]
    },
    {
      id: 5,
      title: "Recognition from the Ministry of Foreign Affairs, Bangladesh",
      shortDescription: "Issued by Ministry of Foreign Affairs, Bangladesh\nJune 2023",
      logo: "/assets/img/others/ministry_of_foreign_affairs_bangladesh_logo.jpg",
      logoAlt: "Ministry of Foreign Affairs, Bangladesh logo",
      description: "I was invited to talk about multiple open-source linguistic project initiatives and represent BengaliAI to the Ministry of Foreign Affairs of Bangladesh.",
      images: [
        { 
          src: "/assets/img/others/rec1.jpg", 
          alt: "" 
        },
        { 
          src: "/assets/img/others/rec2.jpg", 
          alt: "" 
        }
      ]
    },
        {
      id: 6,
      title: "Invited Speaker – Talk with Researchers: Bengali Language in NLP",
      shortDescription: "Andromeda Space & Robotics Research Organization - August 2023",
      logo: "/assets/img/others/asrro_logo.jpg",
      logoAlt: "Andromeda Space & Robotics Research Organization logo",
      description: `<p>Was invited to be a speaker at a webinar on AI and Bangla NLP organized by Andromeda Space & Robotics Research Organization from CUET</p>`,
      images: [
        { 
          src: "/assets/img/others/rec3.jpg", 
          alt: "" 
        },
              
      ]
    },
    {
      id: 7,
      title: "Invited Speaker – AI Research & Academic Development Webinar",
      shortDescription: "BRAC University Computer Club - December 2023",
      logo: "/assets/img/others/bucc_logo.jpg",
      logoAlt: "BRAC University Computer Club logo",
      description: `<p>Was invited to be a speaker at a webinar on AI, Bangla NLP, and the importance of doing impactful research, and how a student should pick good topics for their undergrad thesis, organized by BRAC University Computer Club</p>`,
      images: [
        { 
          src: "/assets/img/others/rec4.jpg", 
          alt: "" 
        },
      ]
    },
    {
      id: 8,
      title: "Invited Speaker – AI Symposium : Exploring Bangla NLP with Bengali.AI",
      shortDescription: "Tech Topia - May 2023",
      logo: "/assets/img/others/ttl.jpg",
      logoAlt: "Tech Topia logo",
      description: `<p>Got invited as a speaker for Talk about Bangla NLP organized by TechTopia</p>`,
      images: [
        {
          src: "/assets/img/others/rec5.jpg",
          alt: ""
        },
      ]
    },
            {
      id: 9,
      title: "Adjudicator",
      shortDescription: "BRAC University Electrical and Electronic Club\nMarch 2021",
      logo: "/assets/img/others/bueec_logo.jpg",
      logoAlt: "BRAC University EEE Club logo",
      description: `<p>Was invited to evaluate the writings of the participants in a tech write-up contest "Electroditor" organized by Brac University Electrical and Electronics Club.</p>`
    },
  ];

  const renderSimpleCards = (items: SimpleResumeItem[], tab?: string) => {
    if (!items || items.length === 0) {
      return (
        <div className="resume-card" data-aos="fade-up" data-aos-delay="0">
          <div className="card-body">
            <p className="card-description">No records added yet.</p>
          </div>
        </div>
      );
    }
    
    const currentTab = tab || '';
    return items.map((item, index) => (
      <div key={item.id} className="resume-card" data-aos="fade-up" data-aos-delay={index * 100}>
        <div className="card-header">
          {item.logo && (
            <div className="card-logo">
              <OptimizedImage
                src={item.logo}
                alt={item.logoAlt || `${item.title} logo`}
                aspectRatio="1/1"
                objectFit="contain"
              />
            </div>
          )}
          <div className="card-info">
            <h3 className="card-title">{item.title}</h3>
            {item.shortDescription && (
              <div className="card-short-description">
                {currentTab === 'volunteering' && item.shortDescription.includes('•') ? (
                  <>
                    <div className="organization" style={{ color: '#9ca3af' }}>{item.shortDescription.split('•')[0].trim()}</div>
                    <div className="date-line" style={{ display: 'block', marginTop: '8px', fontStyle: 'normal', color: '#9ca3af', fontWeight: '400', fontSize: '14px', fontFamily: 'Poppins, sans-serif' }}>{item.shortDescription.split('•').slice(1).join('•').trim()}</div>
                  </>
                ) : (currentTab === 'awards' || currentTab === 'achievements') && (item.shortDescription.includes('\n') || item.shortDescription.includes(' - ')) ? (
                  <>
                    {item.shortDescription.includes('\n') ? (
                      <>
                        <div style={{ color: '#9ca3af' }}>{item.shortDescription.split('\n')[0]}</div>
                        <div style={{
                          display: 'block',
                          marginTop: '8px',
                          fontStyle: 'normal',
                          color: '#9ca3af',
                          lineHeight: '1.4',
                          fontWeight: '400',
                          fontSize: '14px',
                          fontFamily: 'Poppins, sans-serif'
                        }}>
                          {item.shortDescription.split('\n')[1]}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ color: '#9ca3af' }}>{item.shortDescription.split(' - ')[0]}</div>
                        <div style={{
                          display: 'block', 
                          marginTop: '8px',
                          fontStyle: 'normal',
                          color: '#9ca3af',
                          lineHeight: '1.4',
                          fontWeight: '400',
                          fontSize: '14px',
                          fontFamily: 'Poppins, sans-serif'
                        }}>
                          {item.shortDescription.split(' - ')[1]}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div style={{ color: '#9ca3af', fontSize: '14px', fontFamily: 'Poppins, sans-serif', fontWeight: '400', lineHeight: '1.4' }}>
                    {item.shortDescription}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="card-body">
          {item.description && (
            <div className="card-description">
              {Array.isArray(item.description) ? (
                <p>{item.description.join(' ')}</p>
              ) : (
                <div dangerouslySetInnerHTML={{ 
                  __html: item.description
                    .split('\n\n')
                    .map(para => para.trim() ? `<p>${para}</p>` : '')
                    .join('')
                    .replace(/\n(\d+)\.\s/g, '<br/>$1. ')
                }} />
              )}
            </div>
          )}
          {item.images && item.images.length > 0 && (
            <div className="card-images-gallery">
              <h4>Gallery</h4>
              <div className="card-images">
                {item.images.map((img, i) => (
                  <div
                    key={i}
                    className="gallery-item" 
                    onClick={() => {
                      setModalImage(img.src);
                      setModalAlt(img.alt);
                      setCurrentGalleryImages(item.images || []);
                      setCurrentImageIndex(i);
                    }}
                  >
                    <div className="gallery-item-inner">
                      <img src={img.src} alt={img.alt} />
                      <div className="gallery-item-overlay">
                        <span className="zoom-icon">
                          <i className="bi bi-zoom-in"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    ));
  };

  return (
    <section id="resume" className="resume section">
      <div className="container">
        {/* Tab Navigation */}
        <div className="resume-tabs" data-aos="fade-up">
          <div className="tab-buttons">
            <button
              className={`tab-button ${activeTab === 'experience' ? 'active' : ''}`}
              onClick={() => setActiveTab('experience')}
            >
              <i className="bi bi-briefcase"></i>
              Experience
            </button>
            <button
              className={`tab-button ${activeTab === 'education' ? 'active' : ''}`}
              onClick={() => setActiveTab('education')}
            >
              <i className="bi bi-mortarboard"></i>
              Education
            </button>
            <button
              className={`tab-button ${activeTab === 'achievement' ? 'active' : ''}`}
              onClick={() => setActiveTab('achievement')}
            >
              <i className="bi bi-trophy"></i>
              Achievement
            </button>
            <button
              className={`tab-button ${activeTab === 'volunteering' ? 'active' : ''}`}
              onClick={() => setActiveTab('volunteering')}
            >
              <i className="bi bi-people"></i>
              Volunteering
            </button>
            <button
              className={`tab-button ${activeTab === 'awards' ? 'active' : ''}`}
              onClick={() => setActiveTab('awards')}
            >
              <i className="bi bi-award"></i>
              Awards and Honors
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="resume-content" data-aos="fade-up" data-aos-delay="100">
          {activeTab === 'experience' && (
            <div className="experience-section">
              {(() => {
                const groupedExperiences: { [key: string]: typeof experienceData } = {};
                experienceData.forEach(exp => {
                  if (!groupedExperiences[exp.company]) {
                    groupedExperiences[exp.company] = [];
                  }
                  groupedExperiences[exp.company].push(exp);
                });

                return Object.entries(groupedExperiences).map(([company, experiences], companyIndex) => (
                  <div key={company} className="resume-card company-group" data-aos="fade-up" data-aos-delay={companyIndex * 100}>
                    {experiences.map((exp, positionIndex) => (
                      <div key={exp.id} className="position-entry">
                        {/* Divider line between positions (except for the first one) */}
                        {positionIndex > 0 && (
                          <div className="position-divider">
                            <hr className="role-separator" />
                          </div>
                        )}
                        
                        <div className="card-header">
                          <div className="card-header-top">
                            {/* Show logo only for the first position in each company */}
                            {exp.logo && positionIndex === 0 && (
                              <div className="card-logo">
                                <OptimizedImage
                                  src={exp.logo}
                                  alt={exp.logoAlt || `${exp.company} logo`}
                                  aspectRatio="1/1"
                                  objectFit="contain"
                                />
                              </div>
                            )}
                            <div className="card-info">
                              <h3 className="card-title">{exp.title}</h3>
                              <h4 className="card-company">
                                {exp.companyUrl ? (
                                  <a href={exp.companyUrl} target="_blank" rel="noopener noreferrer">
                                    {exp.company}
                                  </a>
                                ) : (
                                  exp.company
                                )}
                              </h4>
                              <div className="employment-details">
                                <span className="location">{exp.location}</span>
                              </div>
                            </div>
                          </div>
                          <div className="card-employment-details">
                            <div className="card-meta">
                              <span>{exp.employmentType}</span>
                              <span className="separator">·</span>
                              <span>{exp.locationType}</span>
                            </div>
                            <div className="card-period">
                              <span className="period">{exp.period}</span>
                            </div>
                          </div>
                        </div>
                  
                  <div className="card-body">
                    <div 
                      className="card-description" 
                      dangerouslySetInnerHTML={{ 
                        __html: exp.description.split('\n\n').map(para => `<p>${para}</p>`).join('') 
                      }} 
                    />

                    {exp.responsibilities && (
                      <div className="responsibilities">
                        <strong>Key Responsibilities:</strong>
                        <ul>
                          {exp.responsibilities.map((resp, idx) => (
                            <li key={idx}>{resp}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {exp.projects && (
                      <div className="projects">
                        <strong>Project Involvements:</strong>
                        <ul>
                          {exp.projects.map((project, idx) => (
                            <li key={idx}>{project}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {exp.supervisors && (
                      <div className="supervisors">
                        <strong>Reporting Supervisors:</strong>
                        <div className="supervisor-links">
                          {exp.supervisors.map((supervisor, idx) => (
                            <a key={idx} href={supervisor.link} target="_blank" rel="noopener noreferrer">
                              {supervisor.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {exp.otherSupervisors && (
                      <div className="supervisors">
                        <strong>Remote Supervisors:</strong>
                        <div className="supervisor-links">
                          {exp.otherSupervisors.map((supervisor, idx) => (
                            <a key={idx} href={supervisor.link} target="_blank" rel="noopener noreferrer">
                              {supervisor.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                        </div>
                      </div>
                    ))}
                  </div>
                ));
              })()}
            </div>
          )}

          {activeTab === 'education' && (
            <div className="education-section">
              {educationData.map((edu, index) => (
                <div key={edu.id} className="resume-card" data-aos="fade-up" data-aos-delay={index * 100}>
                  <div className="card-header">
                    {edu.logo && (
                      <div className="card-logo">
                        <OptimizedImage
                          src={edu.logo}
                          alt={edu.logoAlt || `${edu.institution} logo`}
                          aspectRatio="1/1"
                          objectFit="contain"
                        />
                      </div>
                    )}
                    <div className="card-info">
                      <h3 className="card-title">{edu.degree}</h3>
                      <h4 className="card-company">{edu.field}</h4>
                      <div className="card-meta">
                        <span className="institution">{edu.institution}</span>
                      </div>
                      <div className="card-meta">
                        <span className="location">{edu.location}</span>
                      </div>
                      <div className="card-period">
                        <span className="period">{edu.period}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card-body">
                    <div className="thesis-info">
                      <strong>Thesis:</strong>
                      <a href={edu.thesis.link} target="_blank" rel="noopener noreferrer">
                        {edu.thesis.title}
                      </a>
                    </div>

                    <div className="supervisor-info">
                      <strong>Supervisor:</strong>
                      <a href={edu.supervisor.link} target="_blank" rel="noopener noreferrer">
                        {edu.supervisor.name}
                      </a>
                    </div>

                    <div className="publication-info">
                      <strong>Publication:</strong>
                      {typeof edu.publication === 'string' ? (
                        <span>{edu.publication}</span>
                      ) : (
                        <a href={edu.publication.link} target="_blank" rel="noopener noreferrer">
                          {edu.publication.title}
                        </a>
                      )}
                    </div>

                    <div className="concentration-info">
                      <strong>Concentration:</strong>
                      <span>{edu.concentration}</span>
                    </div>

                    <div className="courses-section">
                      <strong>Courses:</strong>
                      <div className="courses-list">
                        {edu.courses.map((course, idx) => (
                          <span key={idx} className="course-item">{course}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'achievement' && (
            <div className="achievement-section" data-tab="achievements">
              {renderSimpleCards(achievementData, 'achievements')}
            </div>
          )}

          {activeTab === 'volunteering' && (
            <div className="volunteering-section">
              {renderSimpleCards(volunteeringData, 'volunteering')}
            </div>
          )}

          {activeTab === 'awards' && (
            <div className="awards-section" data-tab="awards">
              {renderSimpleCards(awardsData, 'awards')}
            </div>
          )}
        </div>
      </div>
      
      {/* Image Modal for Gallery */}
      <ImageModal 
        isOpen={modalImage !== null}
        imageUrl={modalImage || ''}
        alt={modalAlt}
        onClose={() => setModalImage(null)}
        images={currentGalleryImages}
        currentImageIndex={currentImageIndex}
        onNavigate={(newIndex) => {
          const newImage = currentGalleryImages[newIndex];
          if (newImage) {
            setCurrentImageIndex(newIndex);
            setModalImage(newImage.src);
            setModalAlt(newImage.alt);
          }
        }}
      />
    </section>
  );
};

export default Resume; 